import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import { startOfWeek, format } from 'date-fns';
import { parseCalendarDate } from '../utils/dateTime';
import {
  getActivityCount,
  getBinKey,
  enumerateBins,
  getDataTypeLabel,
  MESSAGE_TYPE_FIELDS,
} from '../utils/chartBinning';
import DrillDownModal from './DrillDownModal';
import './DataChart.css';

function getRecordsForBinKey(records, binKey, binning) {
  if (!records || !binKey) return [];
  return records.filter(record => {
    const ts = record.timestamp || record.data?.created_date || record.Data?.created_date;
    if (!ts) return false;
    const date = parseCalendarDate(ts);
    if (!date) return false;
    return getBinKey(date, binning) === binKey;
  });
}

function formatBinLabel(binDate, binning) {
  if (!binDate || isNaN(binDate.getTime())) return 'Unknown';
  switch (binning) {
    case 'month':
      return format(binDate, 'MMMM yyyy');
    case 'week':
      return `Week of ${format(startOfWeek(binDate, { weekStartsOn: 0 }), 'MMM d, yyyy')}`;
    case 'day':
    default:
      return format(binDate, 'MMM d, yyyy');
  }
}

// Stable color per data type so a series keeps the same color whether it's
// shown alone or overlaid with others.
const SERIES_COLORS = {
  referrals: 'rgb(20, 184, 166)',
  vitalrounds: 'rgb(139, 92, 246)',
  messages: 'rgb(245, 158, 11)',
  'group-referrals': 'rgb(236, 72, 153)',
  logins: 'rgb(54, 162, 235)',
  studies: 'rgb(16, 185, 129)',
  'study-details': 'rgb(239, 68, 68)',
  faxes: 'rgb(99, 102, 241)',
};
const FALLBACK_COLORS = [
  'rgb(20, 184, 166)', 'rgb(255, 99, 132)', 'rgb(54, 162, 235)',
  'rgb(255, 206, 86)', 'rgb(75, 192, 192)', 'rgb(153, 102, 255)',
  'rgb(255, 159, 64)', 'rgb(199, 199, 199)',
];

function axisIdFor(id) {
  return `y_${String(id).replace(/[^a-zA-Z0-9]/g, '_')}`;
}

export function getSeriesColor(id, index = 0) {
  return SERIES_COLORS[id] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

function aggregateRecords(records, seriesId, binning, fieldsOverride) {
  const agg = {};
  (records || []).forEach(record => {
    const ts = record.timestamp || record.data?.created_date;
    if (!ts) return;
    const date = parseCalendarDate(ts);
    if (!date) return;
    const binKey = getBinKey(date, binning);
    agg[binKey] = (agg[binKey] || 0) + getActivityCount(record, seriesId, fieldsOverride);
  });
  return agg;
}

// When a single message channel is chosen, isolate it; otherwise let
// getActivityCount fall back to the combined rollup fields.
function messageFieldsFor(seriesId, messageField) {
  return seriesId === 'messages' && messageField ? [messageField] : undefined;
}

function seriesLabelFor(seriesId, messageField) {
  const base = getDataTypeLabel(seriesId);
  if (seriesId === 'messages' && messageField) {
    const meta = MESSAGE_TYPE_FIELDS.find(m => m.field === messageField);
    if (meta) return `${base} — ${meta.label}`;
  }
  return base;
}

/**
 * Overlays multiple data types as one line each on a shared time axis.
 *
 * When `comparison` is supplied, the chart switches into year-over-year mode:
 * it renders the FIRST series for the current window (solid) plus the same
 * series for the prior window (dashed), index-aligned so the shapes overlay
 * on a single shared axis.
 *
 * @param {Array<{id:string, records:Array}>} seriesData  selected types + their (already facility-filtered) records
 * @param {string} binning  'day' | 'week' | 'month'
 * @param {boolean} sharedScale  true = one y-axis for all; false = each series auto-scaled on its own hidden axis (compare shape)
 * @param {{records:Array, windowStart:string, windowEnd:string, label:string}|null} comparison  prior-period data for YoY mode
 */
function CombinedTrendChart({ seriesData, binning, windowStart, windowEnd, sharedScale = false, comparison = null, messageField = null }) {
  const chartRef = useRef(null);
  const [drillDown, setDrillDown] = useState(null);
  // datasetIndex -> { id, records } so a click can resolve which data type
  // (and its underlying rows) was clicked.
  const seriesOrderRef = useRef([]);
  // YoY drill-down support: bin keys of the prior window aligned to the
  // current-window x positions, plus the (filtered) prior records.
  const priorBinKeysRef = useRef([]);
  const comparingRef = useRef(false);

  const usable = useMemo(
    () => (seriesData || []).filter(s => Array.isArray(s.records)),
    [seriesData]
  );
  seriesOrderRef.current = usable;

  const comparing = !!(comparison && Array.isArray(comparison.records) && usable.length >= 1);
  comparingRef.current = comparing;

  const chartData = useMemo(() => {
    if (usable.length === 0) return null;

    // ---- Year-over-year comparison: one series, two lines ----
    if (comparing) {
      const base = usable[0];
      const color = getSeriesColor(base.id, 0);
      const baseFields = messageFieldsFor(base.id, messageField);

      const curBins = enumerateBins(windowStart, windowEnd, binning) || [];
      const priorBins = enumerateBins(comparison.windowStart, comparison.windowEnd, binning) || [];
      const aggCur = aggregateRecords(base.records, base.id, binning, baseFields);
      const aggPrior = aggregateRecords(comparison.records, base.id, binning, baseFields);

      // Use the current window's bins as the x-axis; align the prior series
      // to the same positions by index (1st bin vs 1st bin, etc.).
      const labels = curBins.length ? curBins : priorBins;
      const curCounts = labels.map((_, i) => (curBins[i] != null ? (aggCur[curBins[i]] || 0) : null));
      const priorCounts = labels.map((_, i) => (priorBins[i] != null ? (aggPrior[priorBins[i]] || 0) : null));
      priorBinKeysRef.current = labels.map((_, i) => priorBins[i] || null);

      const typeLabel = seriesLabelFor(base.id, messageField);
      const datasets = [
        {
          label: `${typeLabel} — this period`,
          data: curCounts,
          borderColor: color,
          backgroundColor: color,
          yAxisID: 'y',
          tension: 0.3,
          fill: false,
          pointRadius: labels.length > 60 ? 0 : 2,
          borderWidth: 2,
        },
        {
          label: `${typeLabel} — ${comparison.label || 'previous year'}`,
          data: priorCounts,
          borderColor: color,
          backgroundColor: color,
          borderDash: [6, 4],
          yAxisID: 'y',
          tension: 0.3,
          fill: false,
          pointRadius: labels.length > 60 ? 0 : 2,
          pointStyle: 'rectRot',
          borderWidth: 2,
        },
      ];

      return { labels, datasets };
    }

    // ---- Standard multi-series overlay ----
    const aggBySeries = {};
    usable.forEach(s => {
      aggBySeries[s.id] = aggregateRecords(s.records, s.id, binning, messageFieldsFor(s.id, messageField));
    });

    let bins = enumerateBins(windowStart, windowEnd, binning);
    if (!bins) {
      const all = new Set();
      Object.values(aggBySeries).forEach(agg => Object.keys(agg).forEach(k => all.add(k)));
      bins = Array.from(all).sort();
    }

    const datasets = usable.map((s, i) => {
      const color = SERIES_COLORS[s.id] || FALLBACK_COLORS[i % FALLBACK_COLORS.length];
      const counts = bins.map(b => aggBySeries[s.id][b] || 0);
      return {
        label: seriesLabelFor(s.id, messageField),
        data: counts,
        borderColor: color,
        backgroundColor: color,
        yAxisID: sharedScale ? 'y' : axisIdFor(s.id),
        tension: 0.3,
        fill: false,
        pointRadius: bins.length > 60 ? 0 : 2,
        borderWidth: 2,
      };
    });

    return { labels: bins, datasets };
  }, [usable, binning, windowStart, windowEnd, sharedScale, comparing, comparison, messageField]);

  const handleChartClick = useCallback((event) => {
    const chart = chartRef.current;
    if (!chart) return;
    const nativeEvent = event.nativeEvent || event;
    const elements = chart.getElementsAtEventForMode(nativeEvent, 'nearest', { intersect: true }, false);
    if (elements.length === 0) return;

    const { index, datasetIndex } = elements[0];
    const label = chart.data.labels[index];
    if (label == null) return;

    // In YoY mode the 2nd dataset is the prior period: its bins live in a
    // different year, so resolve the click against the aligned prior bin key
    // and the prior series records rather than the current-window labels.
    const isPriorDataset = comparingRef.current && datasetIndex === 1;
    const series = isPriorDataset ? seriesOrderRef.current[0] : seriesOrderRef.current[datasetIndex];
    if (!series) return;

    const sourceRecords = isPriorDataset ? (comparison?.records || []) : series.records;

    const binKey = isPriorDataset
      ? priorBinKeysRef.current[index]
      : (typeof label === 'string' && /^\d{4}-\d{2}-\d{2}/.test(label)
          ? label.slice(0, 10)
          : (() => {
              const parsed = parseCalendarDate(label);
              return parsed ? getBinKey(parsed, binning) : String(label);
            })());
    if (!binKey) return;

    const records = getRecordsForBinKey(sourceRecords, binKey, binning);
    if (records.length === 0) return;

    const parts = binKey.split('-');
    const localDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    const chartPointValue = chart.data.datasets?.[datasetIndex]?.data?.[index];
    const suffix = isPriorDataset ? ` — ${comparison?.label || 'previous year'}` : '';

    setDrillDown({
      records,
      dataType: series.id,
      label: `${formatBinLabel(localDate, binning)} — ${getDataTypeLabel(series.id)}${suffix}`,
      chartPointValue: typeof chartPointValue === 'number' ? chartPointValue : null,
    });
  }, [binning, comparison]);

  const zoomBy = useCallback((factor) => {
    const c = chartRef.current;
    if (c?.zoom) c.zoom(factor);
  }, []);
  const resetZoom = useCallback(() => {
    const c = chartRef.current;
    if (c?.resetZoom) c.resetZoom();
  }, []);

  const options = useMemo(() => {
    const timeUnit = binning === 'week' ? 'week' : binning === 'month' ? 'month' : 'day';
    const scales = {
      x: {
        type: 'time',
        time: { unit: timeUnit },
        title: { display: true, text: 'Date' },
        grid: { display: false },
      },
    };

    if (comparing || sharedScale) {
      // YoY compares the same metric across two windows, so a single shared
      // axis is the only meaningful scale.
      scales.y = {
        beginAtZero: true,
        title: { display: true, text: 'Count' },
        ticks: { precision: 0 },
      };
    } else {
      // One hidden, independently-scaled axis per series so each line uses
      // the full height — lets the user compare the SHAPE of trends with
      // wildly different magnitudes (e.g. logins vs referrals). Real counts
      // still show in the tooltip.
      (seriesData || []).forEach(s => {
        scales[axisIdFor(s.id)] = {
          display: false,
          beginAtZero: true,
        };
      });
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'top' },
        title: {
          display: true,
          text: comparing
            ? 'Year-over-Year Comparison'
            : (sharedScale ? 'Trends Over Time' : 'Trends Over Time (shape comparison)'),
          font: { size: 18, weight: 'bold' },
        },
        tooltip: {
          callbacks: {
            title: (context) => {
              try {
                const date = new Date(context[0].label);
                if (isNaN(date.getTime())) return context[0].label;
                if (binning === 'week') return `Week of ${date.toLocaleDateString()}`;
                if (binning === 'month') {
                  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
                }
                return date.toLocaleDateString();
              } catch {
                return context[0].label;
              }
            },
            // In YoY mode, annotate the prior line with its real date so the
            // user knows which historical bin maps onto this x position.
            label: comparing
              ? (context) => {
                  const base = `${context.dataset.label}: ${context.parsed.y}`;
                  if (context.datasetIndex === 1) {
                    const priorKey = priorBinKeysRef.current[context.dataIndex];
                    if (priorKey) return `${base}  (${priorKey})`;
                  }
                  return base;
                }
              : undefined,
          },
        },
        zoom: {
          pan: { enabled: true, mode: 'x' },
          zoom: {
            // Ctrl/⌘+scroll only, so page-scroll over the chart doesn't zoom.
            wheel: { enabled: true, modifierKey: 'ctrl' },
            drag: { enabled: false },
            pinch: { enabled: true },
            mode: 'x',
          },
        },
      },
      scales,
    };
  }, [binning, sharedScale, seriesData, comparing]);

  if (!chartData) {
    return <div className="chart-error">No data to display</div>;
  }

  return (
    <div className="data-chart-container">
      <div className="combined-chart-toolbar">
        <span className="chart-hint chart-hint-inline">Click a point to drill down · +/− or Ctrl+scroll to zoom · Drag to pan</span>
        <div className="chart-zoom-controls" role="group" aria-label="Zoom">
          <button type="button" className="chart-zoom-btn" onClick={() => zoomBy(1.2)} title="Zoom in" aria-label="Zoom in">+</button>
          <button type="button" className="chart-zoom-btn" onClick={() => zoomBy(0.8)} title="Zoom out" aria-label="Zoom out">−</button>
          <button type="button" className="chart-zoom-btn chart-zoom-reset" onClick={resetZoom} title="Reset zoom" aria-label="Reset zoom">Reset</button>
        </div>
      </div>
      <div className="chart-canvas-area">
        <Line ref={chartRef} data={chartData} options={options} onClick={handleChartClick} />
      </div>

      {drillDown && (
        <DrillDownModal
          records={drillDown.records}
          dataType={drillDown.dataType}
          binLabel={drillDown.label}
          chartPointValue={drillDown.chartPointValue}
          onClose={() => setDrillDown(null)}
        />
      )}
    </div>
  );
}

export default CombinedTrendChart;
